// API of the SEO improvement plan module (ADR-0003), /api/seo-plan.
import { request } from '@/api/client'

export type Impact = 'high' | 'medium' | 'low'
export type ActionStatus = 'open' | 'in_progress' | 'ignored' | 'resolved'

export interface SeoPlanRun {
  id: number
  dossier_id: number
  status: 'running' | 'done' | 'failed'
  started_at: string
  finished_at: string | null
  error: string | null
  pages_crawled: number
}

export interface SeoPlanAction {
  id: number
  rule_id: string
  phase: string
  impact: Impact
  title: string
  fix: string
  guidance_url: string | null
  guidance_label: string | null
  evidence: { pages?: string[]; total?: number; [key: string]: unknown }
  affected_count: number
  priority: number
  status: ActionStatus
  resolved_at: string | null
  updated_at: string
}

/** Search Console totals of the last run, against the 28 days before. */
export interface SearchSummary {
  property: string | null
  window: [string, string] | null
  clicks: number | null
  clicks_previous: number | null
  impressions: number | null
  impressions_previous: number | null
  ctr: number | null
  position: number | null
  position_previous: number | null
  error: 'not_connected' | 'no_access' | 'api_error' | null
  detail: string | null
}

export interface CrawlerAccess {
  baseline: { status: number | null; challenged: boolean; error: string | null }
  bots: {
    bot: string
    purpose: 'search' | 'ai_search' | 'training'
    robots_allowed: boolean
    status?: number | null
    challenged?: boolean
    error?: string | null
  }[]
}

export interface SeoPlanSiteSummary {
  dossier_id: number
  name: string
  url: string
  last_run: SeoPlanRun | null
  open_high: number
  open_medium: number
  open_low: number
  resolved: number
  top_actions: string[]
  search: SearchSummary | null
}

export interface SeoPlanSiteDetail {
  dossier_id: number
  name: string
  url: string
  last_run: SeoPlanRun | null
  last_done_run: SeoPlanRun | null
  phases: Record<string, string>
  search: SearchSummary | null
  crawlers: CrawlerAccess | null
  actions: SeoPlanAction[]
}

export interface SeoPlanPageRow {
  url: string
  status: number | null
  title: string | null
  description: string | null
  h1: string | null
  words: number | null
  noindex: boolean
  in_sitemap: boolean
  inlinks: number
  clicks: number | null
  impressions: number | null
  ctr: number | null
  position: number | null
  indexing: string | null
  speed_score: number | null
  lcp_ms: number | null
  top_queries: { query: string; position: number; impressions: number }[]
}

export interface SeoPlanQueryRow {
  query: string
  page: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface SeoPlanInstruction {
  text: string
  actions: number
  platform: 'wordpress' | 'php' | 'other'
}

export const seoPlanApi = {
  listSites: () => request<SeoPlanSiteSummary[]>('/seo-plan/sites'),
  getSite: (dossierId: number) => request<SeoPlanSiteDetail>(`/seo-plan/sites/${dossierId}`),
  startRun: (dossierId: number) =>
    request<SeoPlanRun>(`/seo-plan/sites/${dossierId}/runs`, { method: 'POST' }),
  getRun: (runId: number) => request<SeoPlanRun>(`/seo-plan/runs/${runId}`),
  startAll: () => request<{ started: number; already_running: number }>('/seo-plan/runs', { method: 'POST' }),
  getPages: (dossierId: number) => request<SeoPlanPageRow[]>(`/seo-plan/sites/${dossierId}/pages`),
  getQueries: (dossierId: number) => request<SeoPlanQueryRow[]>(`/seo-plan/sites/${dossierId}/queries`),
  getInstruction: (dossierId: number) =>
    request<SeoPlanInstruction>(`/seo-plan/sites/${dossierId}/claude-instruction`),
  updateAction: (actionId: number, status: Exclude<ActionStatus, 'resolved'>) =>
    request<SeoPlanAction>(`/seo-plan/actions/${actionId}`, { method: 'PATCH', body: { status } }),
}
